import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard, Image, Package, Wrench, Mic, MessageSquare, ImageIcon,
  Info, FileText, Plus, Trash2, Save, LogOut, Loader2, ChevronLeft, Star, Upload,
  Copy, GripVertical, Search, X, Eye, Mail, Archive, CheckCircle, Bell,
  BarChart3, Users, Layers
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: "dashboard", label: "Dashboard", icon: BarChart3 },
  { key: "quotes", label: "Soumissions", icon: FileText },
  { key: "hero_slides", label: "Hero Slides", icon: Image },
  { key: "services", label: "Services", icon: LayoutDashboard },
  { key: "packages", label: "Forfaits", icon: Package },
  { key: "equipment", label: "Équipement", icon: Wrench },
  { key: "voiceover_tracks", label: "Voice Over", icon: Mic },
  { key: "rentals", label: "Location", icon: Package },
  { key: "testimonials", label: "Témoignages", icon: MessageSquare },
  { key: "gallery", label: "Galerie", icon: ImageIcon },
  { key: "partners", label: "Partenaires", icon: Users },
  { key: "site_info", label: "Infos du site", icon: Info },
];

const STATUS_CONFIG = {
  new: { label: "Nouvelle", color: "bg-[#E60000]", text: "text-white" },
  read: { label: "Lue", color: "bg-blue-600", text: "text-white" },
  responded: { label: "Répondue", color: "bg-emerald-600", text: "text-white" },
  archived: { label: "Archivée", color: "bg-zinc-600", text: "text-white" },
};

const inputCls = "bg-black/50 border-red-900/30 text-white h-10 rounded-none focus-visible:ring-2 focus-visible:ring-[#E60000] font-body text-sm";
const labelCls = "text-white/60 text-[10px] uppercase tracking-widest font-body";

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

/* ─── Sortable Item Wrapper ─── */
function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ listeners, isDragging })}
    </div>
  );
}

/* ─── Dashboard Component ─── */
function Dashboard({ newQuoteCount }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`, { headers: getAuthHeaders() })
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#E60000] animate-spin" /></div>;
  if (!stats) return <p className="text-white/40 font-body">Erreur de chargement</p>;

  const statCards = [
    { label: "Soumissions", value: stats.total_quotes, sub: `${stats.new_quotes} nouvelle${stats.new_quotes > 1 ? "s" : ""}`, icon: Mail, accent: stats.new_quotes > 0 },
    { label: "Services", value: stats.counts.services, icon: Layers },
    { label: "Forfaits", value: stats.counts.packages, icon: Package },
    { label: "Galerie", value: stats.counts.gallery, icon: ImageIcon },
    { label: "Témoignages", value: stats.counts.testimonials, icon: MessageSquare },
    { label: "Location", value: stats.counts.rentals, icon: Wrench },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-xl font-bold uppercase text-white tracking-tight mb-6">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        {statCards.map((c) => (
          <div key={c.label} className={`p-4 border ${c.accent ? "border-[#E60000]/40 bg-[#E60000]/5" : "border-white/5 bg-[#0a0a0a]"}`} data-testid={`stat-${c.label.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-2">
              <c.icon size={16} className={c.accent ? "text-[#E60000]" : "text-white/30"} />
              {c.accent && <span className="w-2 h-2 rounded-full bg-[#E60000] animate-pulse" />}
            </div>
            <p className="font-heading text-2xl font-bold text-white">{c.value}</p>
            <p className="font-body text-[10px] uppercase tracking-widest text-white/40">{c.label}</p>
            {c.sub && <p className={`font-body text-xs mt-1 ${c.accent ? "text-[#E60000]" : "text-white/30"}`}>{c.sub}</p>}
          </div>
        ))}
      </div>

      {/* Recent quotes */}
      <h2 className="font-heading text-sm font-bold uppercase text-white tracking-tight mb-3">Dernières soumissions</h2>
      <div className="space-y-2">
        {stats.recent_quotes.length === 0 && <p className="text-white/40 font-body text-sm">Aucune soumission</p>}
        {stats.recent_quotes.map((q, i) => (
          <div key={q.id || i} className="p-3 border border-white/5 bg-[#0a0a0a] flex items-center justify-between">
            <div>
              <p className="font-heading text-sm font-bold text-white">{q.first_name} {q.last_name}</p>
              <p className="font-body text-xs text-[#A3A3A3]">{q.event_type} — {q.event_date}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 font-bold ${STATUS_CONFIG[q.status || "new"]?.color} ${STATUS_CONFIG[q.status || "new"]?.text}`}>
              {STATUS_CONFIG[q.status || "new"]?.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Quotes Manager ─── */
function QuotesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/quotes`, { headers: getAuthHeaders() })
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/quotes/${id}/status`, { status }, { headers: getAuthHeaders() });
    setItems(prev => prev.map(q => q.id === id ? { ...q, status } : q));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette soumission?")) return;
    await axios.delete(`${API}/quotes/${id}`, { headers: getAuthHeaders() });
    load();
  };

  const filtered = items.filter(q => {
    const matchSearch = !search || `${q.first_name} ${q.last_name} ${q.email} ${q.event_type}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || (q.status || "new") === filterStatus;
    return matchSearch && matchStatus;
  });

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/quotes/export/csv`, { headers: getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "soumissions_djmix.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert("Erreur d'export"); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#E60000] animate-spin" /></div>;

  return (
    <div data-testid="admin-quotes">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-xl font-bold uppercase text-white tracking-tight">Soumissions</h1>
        <div className="flex items-center gap-3">
          <button onClick={exportCSV} className="flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 transition-all border border-white/10" data-testid="export-csv-btn">
            <FileText size={14} /> Export CSV
          </button>
          <span className="font-body text-xs text-white/40">{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="quotes-filters">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className={`${inputCls} pl-9`} data-testid="quotes-search" />
          {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"><X size={14} /></button>}
        </div>
        <div className="flex gap-1">
          {["all", "new", "read", "responded", "archived"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold transition-all ${filterStatus === s ? "bg-[#E60000] text-white" : "bg-white/5 text-white/40 hover:text-white"}`}
              data-testid={`filter-${s}`}
            >
              {s === "all" ? "Tout" : STATUS_CONFIG[s]?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quotes list */}
      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-white/40 font-body text-sm py-8 text-center">Aucune soumission trouvée.</p>}
        {filtered.map((q, i) => {
          const st = q.status || "new";
          return (
            <div key={q.id || i} className="p-4 border border-white/5 bg-[#0a0a0a]" data-testid={`quote-item-${i}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-heading text-sm font-bold text-white">{q.first_name} {q.last_name}</p>
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold ${STATUS_CONFIG[st].color} ${STATUS_CONFIG[st].text}`}>
                      {STATUS_CONFIG[st].label}
                    </span>
                  </div>
                  <p className="font-body text-xs text-[#A3A3A3]">{q.email} &bull; {q.phone}</p>
                  <p className="font-body text-xs text-[#E60000] mt-1">{q.event_type} — {q.event_date}</p>
                  <p className="font-body text-xs text-white/50 mt-0.5">{q.location} &bull; {q.num_people} pers. &bull; {q.duration}</p>
                  {q.options?.length > 0 && <p className="font-body text-xs text-white/40 mt-0.5">Options: {q.options.join(", ")}</p>}
                  {q.budget && <p className="font-body text-xs text-white/40">Budget: {q.budget}</p>}
                  {q.message && <p className="font-body text-xs text-white/50 mt-1 italic">&laquo;&nbsp;{q.message}&nbsp;&raquo;</p>}
                  <p className="font-body text-[10px] text-white/20 mt-2">{q.created_at}</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {st !== "read" && <button onClick={() => updateStatus(q.id, "read")} title="Marquer comme lue" className="p-1.5 text-white/30 hover:text-blue-400 transition-colors" data-testid={`mark-read-${i}`}><Eye size={14} /></button>}
                  {st !== "responded" && <button onClick={() => updateStatus(q.id, "responded")} title="Marquer comme répondue" className="p-1.5 text-white/30 hover:text-emerald-400 transition-colors" data-testid={`mark-responded-${i}`}><CheckCircle size={14} /></button>}
                  {st !== "archived" && <button onClick={() => updateStatus(q.id, "archived")} title="Archiver" className="p-1.5 text-white/30 hover:text-zinc-400 transition-colors" data-testid={`mark-archived-${i}`}><Archive size={14} /></button>}
                  <button onClick={() => handleDelete(q.id)} title="Supprimer" className="p-1.5 text-white/30 hover:text-[#E60000] transition-colors" data-testid={`delete-quote-${i}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Site Info Editor ─── */
function SiteInfoEditor() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingField, setUploadingField] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content-with-id/site_info`, { headers: getAuthHeaders() })
      .then(r => setData(r.data || {}))
      .catch(e => console.error("Site info fetch error:", e))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-info`, data, { headers: getAuthHeaders() });
      alert("Sauvegardé!");
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleFileUpload = (field, accept) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      setUploadingField(field);
      setUploadProgress(0);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data: res } = await axios.post(`${API}/admin/upload`, formData, {
          headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => { if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100)); },
        });
        setData(p => ({ ...p, [field]: res.url }));
      } catch (err) {
        alert("Erreur d'upload: " + (err.response?.data?.detail || err.message));
      }
      setUploading(false);
      setUploadingField(null);
      setUploadProgress(0);
    };
    input.click();
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#E60000] animate-spin" /></div>;

  return (
    <div data-testid="admin-site-info">
      <h1 className="font-heading text-xl font-bold uppercase text-white tracking-tight mb-6">Infos du site</h1>
      <div className="space-y-4 max-w-2xl">
        {["company_name", "tagline", "phone", "email", "address", "logo_url"].map(f => (
          <div key={f} className="space-y-1">
            <Label className={labelCls}>{f.replace(/_/g, " ")}</Label>
            <Input value={data[f] || ""} onChange={e => setData(p => ({ ...p, [f]: e.target.value }))} className={inputCls} data-testid={`admin-field-${f}`} />
          </div>
        ))}

        {/* Hero video */}
        <div className="space-y-2 pt-2 border-t border-red-900/20">
          <Label className={labelCls}>Vidéo de fond du Hero</Label>
          <p className="font-body text-[11px] text-white/40 -mt-0.5">Clip court, format MP4 recommandé (max 60 Mo).</p>
          <div className="flex gap-2">
            <Input value={data.hero_video_url || ""} onChange={e => setData(p => ({ ...p, hero_video_url: e.target.value }))} className={`${inputCls} flex-1`} placeholder="URL ou uploadez" data-testid="admin-field-hero_video_url" />
            <button type="button" onClick={() => handleFileUpload("hero_video_url", "video/*")} disabled={uploading} className="flex items-center gap-1.5 bg-[#E60000]/20 border border-[#E60000]/40 hover:bg-[#E60000]/30 disabled:opacity-60 text-[#E60000] text-xs font-bold px-3 py-2 transition-all shrink-0" data-testid="upload-hero-video-btn">
              {uploading && uploadingField === "hero_video_url" ? <><Loader2 className="w-3 h-3 animate-spin" /> {uploadProgress}%</> : <><Upload size={14} /> Vidéo</>}
            </button>
          </div>
          {uploading && uploadingField === "hero_video_url" && (
            <div className="w-full h-1.5 bg-white/10 overflow-hidden"><div className="h-full bg-[#E60000] transition-all duration-200" style={{ width: `${uploadProgress}%` }} /></div>
          )}
          {data.hero_video_url && (
            <div className="flex items-center gap-3">
              <video src={data.hero_video_url.startsWith("/api") ? `${API.replace("/api", "")}${data.hero_video_url}` : data.hero_video_url} className="w-40 h-24 object-cover border border-white/10 bg-black" muted loop autoPlay playsInline />
              <button type="button" onClick={() => setData(p => ({ ...p, hero_video_url: "" }))} className="text-xs text-[#A3A3A3] hover:text-[#E60000] transition-colors flex items-center gap-1"><Trash2 size={12} /> Retirer</button>
            </div>
          )}
        </div>

        <button onClick={handleSave} disabled={saving || !data._id} className="flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-6 py-3 transition-all" data-testid="admin-save-single">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save size={14} /> Sauvegarder</>}
        </button>
      </div>
    </div>
  );
}

/* ─── Content List Manager (with DnD, Search, Duplicate, Preview) ─── */
function ContentListManager({ section }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingField, setUploadingField] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/content-with-id/${section}`, { headers: getAuthHeaders() })
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [section]);

  useEffect(() => { load(); setEditingId(null); setSearch(""); setCategoryFilter("all"); }, [load]);

  const getFields = () => {
    switch (section) {
      case "hero_slides": return ["title", "subtitle", "image", "order"];
      case "services": return ["title", "description", "image", "tags", "span", "order"];
      case "packages": return ["title", "price", "description", "icon", "features", "accent", "order"];
      case "equipment": return ["title", "description", "image", "icon", "order"];
      case "testimonials": return ["name", "role_label", "text", "rating", "order"];
      case "gallery": return ["title", "description", "image", "category", "order"];
      case "voiceover_tracks": return ["title", "description", "audio_url", "order"];
      case "rentals": return ["title", "description", "image", "price", "order"];
      case "partners": return ["title", "image", "url", "order"];
      default: return ["title", "description"];
    }
  };

  const handleAdd = async () => {
    const newItem = { title: "Nouveau", description: "Description", order: items.length };
    if (section === "voiceover_tracks") { newItem.title = "Nouveau track"; newItem.audio_url = ""; }
    if (section === "rentals") { newItem.title = "Nouvel équipement"; newItem.image = ""; newItem.price = "Sur demande"; }
    if (section === "gallery") { newItem.image = ""; newItem.category = "Autres"; }
    if (section === "testimonials") { newItem.name = "Nom"; newItem.role_label = "Rôle"; newItem.text = "Témoignage"; newItem.rating = 5; }
    if (section === "hero_slides") { newItem.image = ""; newItem.subtitle = ""; }
    if (section === "partners") { newItem.title = "Nouveau partenaire"; newItem.image = ""; }
    try {
      await axios.post(`${API}/admin/${section}`, newItem, { headers: getAuthHeaders() });
      load();
    } catch { /* ignore */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet élément?")) return;
    await axios.delete(`${API}/admin/${section}/${id}`, { headers: getAuthHeaders() });
    load();
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/admin/${section}/${id}/duplicate`, {}, { headers: getAuthHeaders() });
      load();
    } catch { /* ignore */ }
  };

  const startEdit = (item) => { setEditingId(item.id); setEditForm({ ...item }); };

  const handleSaveEdit = async (id) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/${section}/${id}`, editForm, { headers: getAuthHeaders() });
      setEditingId(null);
      load();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex(i => i.id === active.id);
    const newIndex = items.findIndex(i => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    try {
      await axios.put(`${API}/admin/${section}/reorder`, { ordered_ids: reordered.map(i => i.id) }, { headers: getAuthHeaders() });
    } catch { load(); }
  };

  const handleFileUpload = (field, accept) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      setUploadingField(field);
      setUploadProgress(0);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await axios.post(`${API}/admin/upload`, formData, {
          headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => { if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100)); },
        });
        setEditForm(p => ({ ...p, [field]: data.url }));
      } catch (err) {
        alert("Erreur: " + (err.response?.data?.detail || err.message));
      }
      setUploading(false);
      setUploadingField(null);
      setUploadProgress(0);
    };
    input.click();
  };

  // Categories for gallery filter
  const categories = section === "gallery" ? [...new Set(items.map(i => i.category).filter(Boolean))] : [];

  const filtered = items.filter(item => {
    const matchSearch = !search || JSON.stringify(item).toLowerCase().includes(search.toLowerCase());
    const matchCat = categoryFilter === "all" || item.category === categoryFilter;
    return matchSearch && matchCat;
  });

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#E60000] animate-spin" /></div>;

  const sectionLabel = SECTIONS.find(s => s.key === section)?.label || section;

  return (
    <div data-testid={`admin-section-${section}`}>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-heading text-xl font-bold uppercase text-white tracking-tight">{sectionLabel}</h1>
        <button onClick={handleAdd} className="flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 transition-all" data-testid="admin-add-btn">
          <Plus size={14} /> Ajouter
        </button>
      </div>

      {/* Search & Category filter */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className={`${inputCls} pl-9`} data-testid="content-search" />
          {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"><X size={14} /></button>}
        </div>
        {categories.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            <button onClick={() => setCategoryFilter("all")} className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold transition-all ${categoryFilter === "all" ? "bg-[#E60000] text-white" : "bg-white/5 text-white/40 hover:text-white"}`}>Tout</button>
            {categories.map(c => (
              <button key={c} onClick={() => setCategoryFilter(c)} className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold transition-all ${categoryFilter === c ? "bg-[#E60000] text-white" : "bg-white/5 text-white/40 hover:text-white"}`}>{c}</button>
            ))}
          </div>
        )}
      </div>

      {/* Items list with drag & drop */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={filtered.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {filtered.length === 0 && <p className="text-white/40 font-body text-sm py-8 text-center">Aucun élément.</p>}
            {filtered.map((item, i) => (
              <SortableItem key={item.id} id={item.id}>
                {({ listeners }) => (
                  <div className="border border-white/5 bg-[#0a0a0a]" data-testid={`admin-item-${i}`}>
                    {editingId === item.id ? (
                      /* Edit Form */
                      <div className="p-4 space-y-3">
                        {getFields().map(f => (
                          <div key={f} className="space-y-1">
                            <Label className={labelCls}>{f}</Label>
                            {f === "description" || f === "text" ? (
                              <Textarea value={Array.isArray(editForm[f]) ? editForm[f].join("\n") : (editForm[f] || "")} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className={`${inputCls} min-h-[60px]`} />
                            ) : f === "tags" || f === "features" ? (
                              <Textarea value={(editForm[f] || []).join("\n")} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value.split("\n").filter(Boolean) }))} className={`${inputCls} min-h-[60px]`} placeholder="Un par ligne" />
                            ) : f === "accent" ? (
                              <div className="flex items-center gap-2">
                                <Checkbox checked={editForm[f] || false} onCheckedChange={v => setEditForm(p => ({ ...p, [f]: v }))} className="border-red-900/50 data-[state=checked]:bg-[#E60000] data-[state=checked]:border-[#E60000] rounded-none" />
                                <span className="font-body text-xs text-[#A3A3A3]">Mis en avant</span>
                              </div>
                            ) : f === "rating" ? (
                              <div className="flex items-center gap-1">
                                {[1,2,3,4,5].map(n => (
                                  <button key={n} onClick={() => setEditForm(p => ({ ...p, rating: n }))} className="p-1"><Star className={`w-4 h-4 ${n <= (editForm.rating || 5) ? "fill-[#E60000] text-[#E60000]" : "text-white/20"}`} /></button>
                                ))}
                              </div>
                            ) : f === "order" ? (
                              <Input type="number" value={editForm[f] ?? 0} onChange={e => setEditForm(p => ({ ...p, [f]: parseInt(e.target.value) || 0 }))} className={inputCls} />
                            ) : f === "category" ? (
                              <div className="flex flex-wrap gap-2">
                                {["Car Show", "Beach Party", "Soirée Corporative", "Foam Party", "Gala", "Autres"].map(cat => (
                                  <button key={cat} type="button" onClick={() => setEditForm(p => ({ ...p, category: cat }))}
                                    className={`px-3 py-2 text-[10px] uppercase tracking-wider font-bold transition-all ${editForm.category === cat ? "bg-[#E60000] text-white" : "bg-white/5 text-white/40 hover:text-white border border-white/10"}`}
                                  >{cat}</button>
                                ))}
                              </div>
                            ) : f === "image" ? (
                              <div className="space-y-2">
                                <div className="flex gap-2">
                                  <Input value={editForm[f] || ""} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className={`${inputCls} flex-1`} placeholder="URL ou uploadez" />
                                  <button type="button" onClick={() => handleFileUpload("image", "image/*")} disabled={uploading} className="flex items-center gap-1.5 bg-[#E60000]/20 border border-[#E60000]/40 hover:bg-[#E60000]/30 disabled:opacity-60 text-[#E60000] text-xs font-bold px-3 py-2 transition-all shrink-0" data-testid="upload-image-btn">
                                    {uploading && uploadingField === "image" ? <><Loader2 className="w-3 h-3 animate-spin" /> {uploadProgress}%</> : <><Upload size={14} /> Photo</>}
                                  </button>
                                </div>
                                {uploading && uploadingField === "image" && <div className="w-full h-1.5 bg-white/10 overflow-hidden"><div className="h-full bg-[#E60000] transition-all duration-200" style={{ width: `${uploadProgress}%` }} /></div>}
                                {editForm[f] && <img src={editForm[f].startsWith("/api") ? `${API.replace("/api", "")}${editForm[f]}` : editForm[f]} alt="" className="w-20 h-20 object-cover border border-white/10" />}
                              </div>
                            ) : f === "audio_url" ? (
                              <div className="space-y-2">
                                <div className="flex gap-2">
                                  <Input value={editForm[f] || ""} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className={`${inputCls} flex-1`} placeholder="URL du MP3 ou uploadez" />
                                  <button type="button" onClick={() => handleFileUpload("audio_url", "audio/*")} disabled={uploading} className="flex items-center gap-1.5 bg-[#E60000]/20 border border-[#E60000]/40 hover:bg-[#E60000]/30 disabled:opacity-60 text-[#E60000] text-xs font-bold px-3 py-2 transition-all shrink-0" data-testid="upload-audio-btn">
                                    {uploading && uploadingField === "audio_url" ? <><Loader2 className="w-3 h-3 animate-spin" /> {uploadProgress}%</> : <><Upload size={14} /> MP3</>}
                                  </button>
                                </div>
                                {uploading && uploadingField === "audio_url" && <div className="w-full h-1.5 bg-white/10 overflow-hidden"><div className="h-full bg-[#E60000] transition-all duration-200" style={{ width: `${uploadProgress}%` }} /></div>}
                                {editForm[f] && <audio controls src={editForm[f].startsWith("/api") ? `${API.replace("/api", "")}${editForm[f]}` : editForm[f]} className="w-full h-8 mt-1" />}
                              </div>
                            ) : f === "url" ? (
                              <Input value={editForm[f] || ""} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className={inputCls} placeholder="https://facebook.com/... ou site web" />
                            ) : (
                              <Input value={editForm[f] || ""} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className={inputCls} />
                            )}
                          </div>
                        ))}
                        <div className="flex gap-2 pt-2">
                          <button onClick={() => handleSaveEdit(item.id)} disabled={saving} className="flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] disabled:opacity-50 text-white text-xs font-bold uppercase px-4 py-2 transition-all">
                            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save size={12} /> Sauvegarder</>}
                          </button>
                          <button onClick={() => setEditingId(null)} className="text-xs text-[#A3A3A3] hover:text-white px-4 py-2 border border-white/10 transition-colors">Annuler</button>
                        </div>
                      </div>
                    ) : (
                      /* List View */
                      <div className="p-3 flex items-center gap-3">
                        <button {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-white/20 hover:text-white/50 touch-none" data-testid={`drag-handle-${i}`}>
                          <GripVertical size={16} />
                        </button>
                        {item.image && <img src={item.image} alt="" className="w-12 h-12 object-cover shrink-0 border border-white/10" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-heading text-sm font-bold text-white truncate">{item.title || item.name || "Sans titre"}</p>
                          <p className="font-body text-xs text-[#A3A3A3] truncate">{item.description || item.text || item.subtitle || item.role_label || ""}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => setPreviewId(previewId === item.id ? null : item.id)} title="Aperçu" className="p-1.5 text-white/30 hover:text-white transition-colors" data-testid={`preview-item-${i}`}><Eye size={14} /></button>
                          <button onClick={() => handleDuplicate(item.id)} title="Dupliquer" className="p-1.5 text-white/30 hover:text-blue-400 transition-colors" data-testid={`duplicate-item-${i}`}><Copy size={14} /></button>
                          <button onClick={() => startEdit(item)} className="text-xs text-[#A3A3A3] hover:text-white px-2.5 py-1.5 border border-white/10 hover:border-white/30 transition-all font-body" data-testid={`edit-item-${i}`}>Modifier</button>
                          <button onClick={() => handleDelete(item.id)} className="p-1.5 text-white/30 hover:text-[#E60000] transition-colors" data-testid={`delete-item-${i}`}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    )}

                    {/* Preview panel */}
                    {previewId === item.id && editingId !== item.id && (
                      <div className="px-4 pb-4 border-t border-white/5" data-testid={`preview-panel-${i}`}>
                        <p className="font-body text-[10px] uppercase tracking-widest text-white/30 mt-3 mb-2">Aperçu</p>
                        <div className="bg-black/40 border border-white/5 p-4 rounded">
                          <div className="flex gap-4">
                            {item.image && <img src={item.image} alt="" className="w-32 h-24 object-cover border border-white/10 shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-heading text-base font-bold text-white">{item.title || item.name}</h3>
                              {item.subtitle && <p className="font-body text-sm text-white/60">{item.subtitle}</p>}
                              {item.price && <p className="font-body text-sm text-[#E60000] font-bold mt-1">{item.price}</p>}
                              {item.role_label && <p className="font-body text-xs text-[#E60000]">{item.role_label}</p>}
                              <p className="font-body text-sm text-white/50 mt-1 line-clamp-3">{item.description || item.text}</p>
                              {item.features?.length > 0 && (
                                <ul className="mt-2 space-y-1">{item.features.map((f, fi) => <li key={fi} className="font-body text-xs text-white/40">• {f}</li>)}</ul>
                              )}
                              {item.tags?.length > 0 && (
                                <div className="flex gap-1 mt-2">{item.tags.map((t, ti) => <span key={ti} className="text-[10px] bg-white/5 text-white/50 px-2 py-0.5">{t}</span>)}</div>
                              )}
                              {item.rating && (
                                <div className="flex gap-0.5 mt-2">{[1,2,3,4,5].map(n => <Star key={n} className={`w-3 h-3 ${n <= item.rating ? "fill-[#E60000] text-[#E60000]" : "text-white/20"}`} />)}</div>
                              )}
                              {item.category && <span className="inline-block mt-2 text-[10px] bg-[#E60000]/10 text-[#E60000] px-2 py-0.5">{item.category}</span>}
                            </div>
                          </div>
                          {item.audio_url && <audio controls src={item.audio_url.startsWith("/api") ? `${API.replace("/api", "")}${item.audio_url}` : item.audio_url} className="w-full h-8 mt-3" />}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

/* ─── Main Admin Panel ─── */
export default function AdminPanel({ user, onLogout }) {
  const [active, setActive] = useState("dashboard");
  const [newQuoteCount, setNewQuoteCount] = useState(0);

  // Fetch new quote count for badge
  useEffect(() => {
    axios.get(`${API}/admin/dashboard`, { headers: getAuthHeaders() })
      .then(r => setNewQuoteCount(r.data?.new_quotes || 0))
      .catch(e => console.error("Quote count error:", e));
  }, [active]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    onLogout();
  };

  const renderContent = () => {
    if (active === "dashboard") return <Dashboard newQuoteCount={newQuoteCount} />;
    if (active === "quotes") return <QuotesManager />;
    if (active === "site_info") return <SiteInfoEditor />;
    return <ContentListManager section={active} />;
  };

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="admin-panel">
      {/* Sidebar */}
      <aside className="w-56 bg-[#0a0a0a] border-r border-red-900/15 flex flex-col min-h-screen shrink-0">
        <div className="p-4 border-b border-red-900/15">
          <a href="/" className="flex items-center gap-2 text-[#A3A3A3] hover:text-white text-xs font-body transition-colors">
            <ChevronLeft size={14} /> Retour au site
          </a>
          <h2 className="font-heading text-sm font-bold uppercase text-white mt-3 tracking-tight">Admin DJMIX</h2>
          <p className="font-body text-[10px] text-[#A3A3A3] mt-0.5">{user?.email}</p>
        </div>

        <nav className="flex-1 p-2 space-y-0.5">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setActive(s.key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs uppercase tracking-wider font-medium transition-all rounded-none ${
                active === s.key
                  ? "bg-[#E60000]/15 text-[#E60000] border-l-2 border-[#E60000]"
                  : "text-[#A3A3A3] hover:text-white hover:bg-white/5"
              }`}
              data-testid={`admin-tab-${s.key}`}
            >
              <s.icon size={14} />
              {s.label}
              {/* Badge for new quotes */}
              {s.key === "quotes" && newQuoteCount > 0 && (
                <span className="ml-auto bg-[#E60000] text-white text-[9px] font-bold w-5 h-5 flex items-center justify-center rounded-full animate-pulse" data-testid="quote-badge">
                  {newQuoteCount}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-red-900/15">
          <button onClick={handleLogout} className="w-full flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-[#E60000] transition-colors" data-testid="admin-logout">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-6 md:p-8 overflow-y-auto max-h-screen">
        <div className="max-w-5xl">
          {renderContent()}
        </div>
      </main>
    </div>
  );
}
